import { ProductReviewElementDataType } from '@/components/sections/builder/elements/body/product-review/type';
import { ElementProps } from '@/components/sections/builder/types';
import { useListProductReview } from '@/hooks/api/storefront/product-detail';
import { useMemo, useState } from 'react';
import { cn } from '@/lib/utils';
import { Star, StarIcon, X } from 'lucide-react';
import { format } from 'date-fns';
import Image from 'next/image';
import ModalImagePreview from '@/components/sections/builder/elements/body/product-detail/components/modal-image-preview';
import { PaginationProductReview } from '@/components/sections/builder/elements/body/product-review/component/pagination';
import { usePaginationSearchParams } from '@/hooks/use-pagination';
import { Skeleton } from '@/components/ui/skeleton';
import FilterProductReview, {
  SORTING_QUERY,
} from '@/components/sections/builder/elements/body/product-review/component/filter-product-review';
import { Button } from '@/components/ui/button';
import { useBuilderStore } from '@/stores/builder';
import { shallow } from 'zustand/shallow';
import { useStoreSetting } from '@/app/(store)/s/[domain]/store-context';
import { Badge } from '@/components/ui/badge';
import { DEFAULT_THEME_COLOR_SCHEME } from '@/components/sections/builder/theme/color';
import { dummyProductReview } from '@/constant/dummyData/dummy-customer-review';

export default function ElementContentProductReview({
  element,
  preview,
  data,
  theme,
}: ElementProps) {
  const { storeSetting } = useStoreSetting();
  const productId = data.product_group_id;
  const reviewSetting = storeSetting?.product_settings?.review_product;

  const [openPreview, setOpenPreview] = useState<boolean>(false);
  const [previewImages, setPreviewImages] = useState<string[]>([]);
  const [previewInitialIndex, setPreviewInitialIndex] = useState<number>(0);

  const [pagination, setPagination] = usePaginationSearchParams();
  const builder = useBuilderStore(
    (state) => ({
      theme: state.theme,
      setTheme: state.setTheme,
    }),
    shallow
  );

  const themeContent = preview ? builder.theme : theme;
  const starColor = themeContent?.productRating?.starColor || DEFAULT_THEME_COLOR_SCHEME.text;

  const content = element.data as ProductReviewElementDataType;
  const reviewsPerPage = content.reviewsPerPage || 5;

  const currentSortValue = pagination.sorting || content.defaultSortBy || 'created-descending';
  const sortConfig =
    SORTING_QUERY.find((item) => item.value === currentSortValue) || SORTING_QUERY[1];

  const queryParams = {
    page: pagination.pageIndex + 1,
    pageSize: reviewsPerPage,
    rating: pagination.rating || '',
    sortBy: sortConfig.sortBy,
    sortDirection: sortConfig.sortDirection,
    'filter.media_type': pagination.mediaType === 'true' ? 'with_media' : undefined,
  };

  const { data: reviewData, isLoading } = useListProductReview(productId, queryParams);

  const reviews =
    preview && (!reviewData?.data || reviewData.totalCount === 0)
      ? dummyProductReview
      : reviewData?.data || [];

  const mediaReviewCount = useMemo(() => {
    if (!reviewData?.data) return 0;
    return reviewData.data.filter((review) => review.attachments && review.attachments.length > 0)
      .length;
  }, [reviewData?.data]);

  const handleRatingFilter = (rating: number) => {
    setPagination({
      rating: String(rating),
      pageIndex: 0,
    });
  };

  const handleResetFilter = () => {
    setPagination({
      rating: '',
      pageIndex: 0,
      mediaType: '',
    });
  };

  const handleSort = (sortValue: string) => {
    setPagination({
      sorting: sortValue,
      pageIndex: 0,
    });
  };

  const handlePageChange = (newPage: number) => {
    setPagination({
      pageIndex: newPage - 1,
    });
  };

  const handleImageClick = (attachments: string[], clickedIndex: number) => {
    setPreviewImages(attachments);
    setPreviewInitialIndex(clickedIndex);
    setOpenPreview(true);
  };

  const handleMediaFilterToggle = () => {
    setPagination({
      mediaType: pagination.mediaType === 'true' ? undefined : 'true',
      pageIndex: 0,
    });
  };

  const handleRemoveFilter = (filterType: 'rating' | 'media') => {
    if (filterType === 'rating') {
      setPagination({
        rating: '',
        pageIndex: 0,
      });
    } else if (filterType === 'media') {
      setPagination({
        mediaType: undefined,
        pageIndex: 0,
      });
    }
  };

  const totalPages = Math.ceil(Number(reviewData?.totalCount) / Number(queryParams.pageSize));

  const filterObj = {
    rating: pagination.rating ? Number(pagination.rating) : undefined,
  };

  const hasActiveFilters = pagination.rating || pagination.mediaType === 'true';

  if (!reviewSetting && !preview) return null;

  return (
    <>
      <section className={cn('background-color-default')}>
        <div className='theme_layout_page_width mx-auto duration-100'>
          <div className='container mx-auto max-w-screen-xl px-4 lg:px-16'>
            <div
              className={cn(
                'flex items-center gap-2 md:my-3 md:gap-3 xl:gap-4',
                content.contentAlign === 'left' && 'justify-between',
                content.contentAlign === 'center' && 'justify-center',
                content.contentAlign === 'right' && 'flex-row-reverse justify-between'
              )}
            >
              <h2
                className={cn(
                  'theme_font_heading text-base font-bold tracking-tight',
                  content.headingSize === 'large' && 'text-xl sm:text-2xl md:text-3xl lg:text-4xl',
                  content.headingSize === 'medium' && 'text-lg sm:text-xl md:text-2xl lg:text-3xl',
                  content.headingSize === 'small' && 'text-base sm:text-lg md:text-xl lg:text-2xl'
                )}
                data-animation='true'
                dangerouslySetInnerHTML={{ __html: content.headingLabel as string }}
              />
            </div>
            <FilterProductReview
              filter={filterObj}
              handleRatingFilter={handleRatingFilter}
              isPreview={preview}
              updateSortBy={handleSort}
              currentSorting={currentSortValue}
              withMedia={pagination.mediaType === 'true'}
              onMediaFilterToggle={handleMediaFilterToggle}
              mediaReviewCount={mediaReviewCount}
              content={content}
              starColor={starColor}
            />
            {!preview && hasActiveFilters && (
              <div className='mb-4 mt-4 flex flex-wrap items-center gap-3'>
                <div className='flex flex-wrap gap-2'>
                  {pagination.rating && (
                    <Button
                      variant='outline'
                      className='items-center rounded-full text-xs lg:text-sm'
                      onClick={() => handleRemoveFilter('rating')}
                    >
                      <span className='flex items-center gap-1'>
                        <StarIcon
                          className='h-3 w-3'
                          style={{
                            color: starColor,
                            fill: starColor,
                          }}
                        />
                        Rating: {pagination.rating}
                      </span>
                      <X className='ml-2 h-3 w-3' />
                    </Button>
                  )}
                  {pagination.mediaType === 'true' && (
                    <Button
                      variant='outline'
                      className='items-center rounded-full text-xs lg:text-sm'
                      onClick={() => handleRemoveFilter('media')}
                    >
                      <span>With Photos</span>
                      <X className='ml-2 h-3 w-3' />
                    </Button>
                  )}
                </div>
                <Button
                  variant='link'
                  className='text-xs underline lg:text-sm'
                  onClick={handleResetFilter}
                >
                  Remove all
                </Button>
              </div>
            )}
            <div className='mt-6 space-y-6'>
              {isLoading && !preview ? (
                <>
                  {Array.from({ length: 3 }).map((_, index) => (
                    <div key={index}>
                      <div className='mb-4 flex gap-x-3'>
                        <Skeleton className='h-[50px] w-[50px] rounded-lg' />
                        <div className='flex flex-1 flex-col'>
                          <div className='flex gap-x-3'>
                            <Skeleton className='h-5 w-48' />
                          </div>
                          <div className='my-2 flex items-center gap-1'>
                            {Array.from({ length: 5 }).map((_, starIndex) => (
                              <Star
                                key={starIndex}
                                className='h-4 w-4 text-gray-200'
                                fill='currentColor'
                              />
                            ))}
                          </div>
                          <Skeleton className='mt-1 h-4 w-full' />
                          <Skeleton className='mt-1 h-4 w-3/4' />
                          <div className='mt-2 flex flex-wrap gap-2'>
                            {Array.from({ length: 3 }).map((_, imgIndex) => (
                              <Skeleton key={imgIndex} className='h-16 w-16 rounded-md' />
                            ))}
                          </div>
                          <div className='flex justify-end'>
                            <Skeleton className='mt-4 h-3 w-32' />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              ) : (
                <>
                  {reviews.map((review) => {
                    if (!review.customer_name || review.rating == null || !review.created_at)
                      return null;

                    return (
                      <div key={review.review_id} className='rounded-2xl p-8 shadow-sm'>
                        <div className='flex items-center justify-between text-sm font-semibold '>
                          <span>{review.customer_name}</span>
                          <span className='text-xs '>
                            {format(new Date(review.created_at ?? ''), 'dd MMMM yyyy')}
                          </span>
                        </div>

                        <div className='mt-1 flex items-center'>
                          {Array.from({ length: 5 }).map((_, i) => {
                            const isFilled = i < review.rating;
                            return (
                              <Star
                                key={i}
                                size={16}
                                style={{
                                  color: isFilled ? starColor : undefined,
                                  fill: isFilled ? starColor : 'none',
                                }}
                              />
                            );
                          })}
                        </div>

                        {review.variations && Object.keys(review.variations).length > 0 && (
                          <div className='mt-2 flex flex-wrap gap-2'>
                            <Badge className='' variant='custom'>
                              {review.variations.title}: {review.variations.label}
                            </Badge>
                            <span className='font-color-default rounded-md px-2 py-1 text-xs'></span>
                          </div>
                        )}

                        <p className='mt-2 text-sm '>{review.descriptions || '-'}</p>

                        {review.attachments && review.attachments.length > 0 && (
                          <div className='mt-3 flex flex-wrap gap-2'>
                            {review.attachments.map((attachmentUrl, index) => (
                              <div
                                key={index}
                                onClick={() => handleImageClick(review.attachments || [], index)}
                                className='h-20 w-20 cursor-pointer overflow-hidden rounded-lg transition-transform hover:scale-105'
                              >
                                <Image
                                  src={attachmentUrl}
                                  alt={`Review attachment ${index + 1}`}
                                  className='h-full w-full object-cover'
                                  width={80}
                                  height={80}
                                />
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </>
              )}

              {reviews.length === 0 && !isLoading && (
                <div className='text-center text-gray-600'>
                  {pagination.rating
                    ? 'No reviews found for this rating filter.'
                    : 'No reviews found.'}
                </div>
              )}
            </div>
            {totalPages > 1 && (
              <PaginationProductReview
                page={pagination.pageIndex + 1}
                handlePageChange={handlePageChange}
                totalPages={totalPages}
              />
            )}
          </div>
        </div>
      </section>

      {openPreview && (
        <ModalImagePreview
          images={previewImages}
          initialIndex={previewInitialIndex}
          isOpen={openPreview}
          onClose={() => setOpenPreview(false)}
        />
      )}

      <style jsx>{`
        .background-color-default {
          background-color: ${content.colorScheme?.background || 'white'};
          color: ${content.colorScheme?.text};
        }

        .font-color-default {
          color: ${content.colorScheme?.text};
        }
      `}</style>
    </>
  );
}
